import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import AdminCostsView from "../components/AdminCostsView";
import AdminMetricsView from "../components/AdminMetricsView";
import AdminChatAnalyticsView from "../components/AdminChatAnalyticsView";
import AdminMissionControlView from "../components/AdminMissionControlView";
import AdminSpeakersView from "../components/AdminSpeakersView";
import AdminActivityView from "../components/AdminActivityView";
import AdminLibraryView from "../components/AdminLibraryView";
import AdminVeraPerformanceView from "../components/AdminVeraPerformanceView";

import { ADMIN_EMAILS } from "../lib/adminEmails";

type AdminView = "mission" | "library" | "activity" | "costs" | "metrics" | "chat" | "speakers" | "vera";

export default function AdminPage() {
  const { user, token, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeView, setActiveView] = useState<AdminView>("library");
  const [costsPeriod, setCostsPeriod] = useState<"day" | "week" | "month" | "all">("week");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const isAdmin = !!user && ADMIN_EMAILS.includes(user.email);

  // Initialize activeView from URL query parameter
  useEffect(() => {
    const viewParam = searchParams.get("view");
    if (viewParam) {
      const validViews: AdminView[] = ["mission", "library", "activity", "costs", "metrics", "chat", "speakers", "vera"];
      if (validViews.includes(viewParam as AdminView)) {
        setActiveView(viewParam as AdminView);
      }
    }
  }, [searchParams]);

  // ── Early returns (all hooks must be above this line) ──
  if (authLoading) return null;
  if (!user || !isAdmin) return <Navigate to="/" replace />;

  const navItems: { view: AdminView; label: string; icon: React.ReactNode }[] = [
    {
      view: "mission",
      label: "Mission Control",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
          <path fillRule="evenodd" d="M4.606 12.97a.75.75 0 0 1-.134 1.051 2.494 2.494 0 0 0-.93 2.437 2.494 2.494 0 0 0 2.437-.93.75.75 0 1 1 1.186.918 3.995 3.995 0 0 1-4.482 1.332.75.75 0 0 1-.461-.461 3.994 3.994 0 0 1 1.332-4.482.75.75 0 0 1 1.052.134Z" clipRule="evenodd" />
          <path fillRule="evenodd" d="M13.703 4.469a.75.75 0 0 1 .177-.755l1.414-1.414a.75.75 0 1 1 1.06 1.06l-1.413 1.415a.75.75 0 0 1-.755.177l-1.028 1.028a5.5 5.5 0 0 1-.577 5.905l-2.829 2.828a.75.75 0 0 1-1.06-1.06l2.828-2.829a4 4 0 0 0 .556-5.018L10.5 7.382a.75.75 0 0 1-.177.755l-1.06 1.06a.75.75 0 1 1-1.061-1.06l1.06-1.061a.75.75 0 0 1 .756-.177l1.686-1.686a5.5 5.5 0 0 1 2-1.744Z" clipRule="evenodd" />
        </svg>
      ),
    },
    {
      view: "library",
      label: "Library",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
          <path d="M10.75 16.82A7.462 7.462 0 0 1 15 15.5c.71 0 1.396.098 2.046.282A.75.75 0 0 0 18 15.06v-11a.75.75 0 0 0-.546-.721A9.006 9.006 0 0 0 15 3a8.963 8.963 0 0 0-4.25 1.065V16.82ZM9.25 4.065A8.963 8.963 0 0 0 5 3c-.85 0-1.673.118-2.454.339A.75.75 0 0 0 2 4.06v11a.75.75 0 0 0 .954.721A7.506 7.506 0 0 1 5 15.5c1.579 0 3.042.487 4.25 1.32V4.065Z" />
        </svg>
      ),
    },
    {
      view: "activity",
      label: "Activity",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
          <path d="M15.5 2A1.5 1.5 0 0 0 14 3.5v13a1.5 1.5 0 0 0 1.5 1.5h1a1.5 1.5 0 0 0 1.5-1.5v-13A1.5 1.5 0 0 0 16.5 2h-1ZM9.5 6A1.5 1.5 0 0 0 8 7.5v9A1.5 1.5 0 0 0 9.5 18h1a1.5 1.5 0 0 0 1.5-1.5v-9A1.5 1.5 0 0 0 10.5 6h-1ZM3.5 10A1.5 1.5 0 0 0 2 11.5v5A1.5 1.5 0 0 0 3.5 18h1A1.5 1.5 0 0 0 6 16.5v-5A1.5 1.5 0 0 0 4.5 10h-1Z" />
        </svg>
      ),
    },
    {
      view: "costs",
      label: "Costs",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
          <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-8-6a.75.75 0 0 1 .75.75v.316a3.78 3.78 0 0 1 1.653.713c.426.33.744.74.925 1.2a.75.75 0 0 1-1.395.55 1.35 1.35 0 0 0-.447-.563 2.187 2.187 0 0 0-.736-.363V9.3c.698.093 1.383.32 1.959.696.787.514 1.29 1.27 1.29 2.13 0 .86-.504 1.616-1.29 2.13-.576.377-1.261.603-1.96.696v.299a.75.75 0 1 1-1.5 0v-.3a3.78 3.78 0 0 1-1.653-.712 2.91 2.91 0 0 1-.755-.762.75.75 0 0 1 1.194-.908c.148.195.323.357.515.482.191.124.418.223.669.29V10.7a4.76 4.76 0 0 1-1.959-.696C5.504 9.49 5 8.735 5 7.875c0-.86.504-1.616 1.29-2.13a4.76 4.76 0 0 1 1.96-.696V4.75A.75.75 0 0 1 10 4Z" clipRule="evenodd" />
        </svg>
      ),
    },
    {
      view: "metrics",
      label: "Metrics",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
          <path d="M12.5 2.75a.75.75 0 0 0-1.5 0v1.5a.75.75 0 0 0 1.5 0v-1.5ZM8.75 8a.75.75 0 0 0 0 1.5h2.5a.75.75 0 0 0 0-1.5h-2.5Z" />
          <path fillRule="evenodd" d="M8.5 4.27a7.25 7.25 0 1 0 3 0V4a.25.25 0 0 1-.25-.25h-2.5A.25.25 0 0 1 8.5 4v.27ZM10 16.25a6.25 6.25 0 1 0 0-12.5 6.25 6.25 0 0 0 0 12.5Z" clipRule="evenodd" />
        </svg>
      ),
    },
    {
      view: "chat",
      label: "Chat Quality",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
          <path fillRule="evenodd" d="M10 2c-2.236 0-4.43.18-6.57.524C1.993 2.755 1 4.014 1 5.426v5.148c0 1.413.993 2.67 2.43 2.902.848.137 1.705.248 2.57.331v3.443a.75.75 0 0 0 1.28.53l3.58-3.579a.78.78 0 0 1 .527-.224 41.202 41.202 0 0 0 5.183-.5c1.437-.232 2.43-1.49 2.43-2.903V5.426c0-1.413-.993-2.67-2.43-2.902A41.289 41.289 0 0 0 10 2Zm0 7a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM7 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm5 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd" />
        </svg>
      ),
    },
    {
      view: "speakers",
      label: "Speakers",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
          <path d="M10 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM3.465 14.493a1.23 1.23 0 0 0 .41 1.412A9.957 9.957 0 0 0 10 18c2.31 0 4.438-.784 6.131-2.1.43-.333.604-.903.408-1.41a7.002 7.002 0 0 0-13.074.003Z" />
        </svg>
      ),
    },
    {
      view: "vera",
      label: "Vera Performance",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
          <path fillRule="evenodd" d="M1 2.75A.75.75 0 0 1 1.75 2h16.5a.75.75 0 0 1 0 1.5H18v8.75A2.75 2.75 0 0 1 15.25 15h-1.072l.798 3.06a.75.75 0 0 1-1.452.38L13.41 18h-6.82l-.114.44a.75.75 0 0 1-1.452-.38L5.823 15H4.75A2.75 2.75 0 0 1 2 12.25V3.5h-.25A.75.75 0 0 1 1 2.75ZM7.373 15l-.391 1.5h6.036l-.392-1.5H7.373Zm5.084-7.06a.75.75 0 1 0-1.06-1.061L9.75 8.525 8.854 7.63a.75.75 0 1 0-1.06 1.06l1.425 1.426a.75.75 0 0 0 1.06 0l2.178-2.177Z" clipRule="evenodd" />
        </svg>
      ),
    },
  ];

  const activeNavItem = navItems.find((item) => item.view === activeView);

  function handleNavClick(view: AdminView) {
    setActiveView(view);
    setSearchParams({ view });
    setMobileNavOpen(false);
  }

  const sidebarContent = (
    <>
      {navItems.map((item) => (
        <button
          key={item.view}
          onClick={() => handleNavClick(item.view)}
          className={`mb-1 w-full rounded-md px-3 py-2 text-left text-sm font-medium ${
            activeView === item.view
              ? "bg-blue-100 text-blue-700"
              : "text-gray-700 hover:bg-gray-100"
          }`}
        >
          <span className="flex items-center gap-2">
            {item.icon}
            {item.label}
          </span>
        </button>
      ))}</>
  );

  return (
    <div className="flex h-[calc(100vh-68px)] flex-col md:flex-row">
      {/* Mobile navigation dropdown */}
      <div className="relative border-b bg-gray-50 p-3 md:hidden">
        <button
          onClick={() => setMobileNavOpen(!mobileNavOpen)}
          className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
        >
          <span className="flex items-center gap-2">
            {activeNavItem?.icon}
            {activeNavItem?.label}
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className={`h-5 w-5 text-gray-400 transition-transform ${mobileNavOpen ? "rotate-180" : ""}`}
          >
            <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
          </svg>
        </button>

        {mobileNavOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setMobileNavOpen(false)} />
            <div className="absolute left-3 right-3 top-full z-20 mt-1 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
              {navItems.map((item) => (
                <button
                  key={item.view}
                  onClick={() => handleNavClick(item.view)}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-sm ${
                    activeView === item.view
                      ? "bg-blue-50 text-blue-700 font-medium"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {item.icon}
                  {item.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden w-56 shrink-0 overflow-y-auto border-r bg-gray-50 p-3 md:block">
        {sidebarContent}
      </aside>

      {/* Main area */}
      <main className="flex-1 overflow-y-auto p-4">
        {activeView === "mission" ? (
          <AdminMissionControlView />
        ) : activeView === "library" ? (
          <AdminLibraryView token={token} isAdmin={isAdmin} />
        ) : activeView === "activity" ? (
          <AdminActivityView />
        ) : activeView === "costs" ? (
          <AdminCostsView costsPeriod={costsPeriod} setCostsPeriod={setCostsPeriod} />
        ) : activeView === "metrics" ? (
          <AdminMetricsView />
        ) : activeView === "chat" ? (
          <AdminChatAnalyticsView />
        ) : activeView === "speakers" ? (
          <AdminSpeakersView />
        ) : activeView === "vera" ? (
          <AdminVeraPerformanceView />
        ) : (
          <AdminActivityView />
        )}
      </main>
    </div>
  );
}
